import { memo, startTransition, useEffect, useMemo, useRef, useState } from 'react';

import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { Splitter, SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { useDebounce } from '@uidotdev/usehooks';
import { cloneDeep, isEqual, sortBy, uniq } from 'lodash';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ChangeExamStatusModel, ExamGridModel, FileModel, PatientModel, WorklistViewModel } from 'models';

import { apiClient } from 'core/api/globals';
import { useBoolean, useEvent, useInterval, useMobileDetection, usePageVisibility, useSignalRQueryInvalidation } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { ColumnState, DateRangeFilter, Page, PageHeader, SelectedState } from 'core/ui';
import type { DateRangeFilterValue } from 'core/ui/DateRangeFilter';
import { hasText } from 'core/utils';

import { AuthenticationScheme, useAuthentication, useCurrentUser } from 'features/auth';
import { ExamGrid } from 'features/exam/fragments';
import { useExamEditWindow, useExamsGridQuery } from 'features/exam/hooks';
import { applyDateRangeFilterToDataState } from 'features/exam/services/date-range-column-service';
import { UploadWindow, ViewFileWindow } from 'features/file/fragments';
import { useSessionLocation } from 'features/location';
import { DEFAULT_FINAL_REPORT_VALUES } from 'features/patient/constants';
import { ShareModal } from 'features/patient/fragments';
import { PatientRoutes } from 'features/patient/routes';
import { useUserSettings } from 'features/settings';
import { SettingsHelpers } from 'features/settings/services/settings-helpers';
import { WellKnownSettingName } from 'features/settings/types/WellKnownSettingName';
import { WorklistSidebar } from 'features/worklist';
import { WorklistSidebarCollapsed } from 'features/worklist/fragments/WorklistSidebarCollapsed';
import { WorklistSortService } from 'features/worklist/services/WorklistSortService';
import { determineWorklistSidebarState } from 'features/worklist/utils';
import { RelativeDateFilterField, RelativeDateFilterType } from 'features/worklist/utils/relative-date-filters';

import { DocumentCategories } from '../constants';
import { ExamsGridService } from '../services';
import { BaseExamGridPageProps, ExamGridColumnsStateChangeEvent, ExamGridDataStateChangeEvent } from '../types';
import ExamEditWindow from './ExamEditWindow';

const AUTO_REFRESH_INTERVAL = 60 * 1000; // 60 sec

export const BaseExamGridPage = memo<BaseExamGridPageProps>(({ featureView, title, selectable = true, showExamToolbar = false, pickDefaultWorklist }) => {
  const navigate = useNavigate();
  const { reactPatientFormPage, legacyBaseUrl, reactUploadPage, userSettings } = useUserSettings(true);
  const { currentUser } = useCurrentUser(true);
  const gridExcelExport = useRef<ExcelExport | null>(null);
  const { activeScheme } = useAuthentication();

  // Mobile detection using consolidated utility
  const { isMobile } = useMobileDetection();

  // Page visibility detection to pause auto-refresh when tab is not active
  const isPageVisible = usePageVisibility();

  // Loading state is now handled by the examsQuery
  const [isWorklistChanging, setIsWorklistChanging] = useState(false);
  const [selectedExams, setSelectedExams] = useState<Record<number, boolean>>({});
  const [allWorklists, setAllWorklists] = useState<WorklistViewModel[] | null>(null);
  const [selectedWorklistId, setSelectedWorklistId] = useState<number | null>(null);
  const [panes, setPanes] = useState<SplitterPaneProps[] | null>(null);
  const [dataState, setDataState] = useState<State | null>(null);
  const [columnsState, setColumnsState] = useState<Record<string, ColumnState> | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [selectedExamId, setSelectedExamId] = useState(0);
  const [_selectedExamStatus, setSelectedExamStatus] = useState('');
  const [isShareModalVisible, { setTrue: setShareModalVisible, setFalse: setShareModalHidden }] = useBoolean(false);
  const [shareExamId, setShareExamId] = useState<number | null>(null);
  const [sharePatientId, setSharePatientId] = useState<number | null>(null);
  const [isDonorViewFinalReportVisible, setIsDonorViewFinalReportVisible] = useState(false);
  const [allCounts, setAllCounts] = useState<Record<string, number>>({});
  const { sessionLocation } = useSessionLocation(false);
  const [isDonorViewFilesVisible, setIsDonorViewFilesVisible] = useState(false);
  const [finalReport, setFinalReport] = useState<FileModel>(DEFAULT_FINAL_REPORT_VALUES);
  const [_isUploadModalVisible, _setIsUploadModalVisible] = useState(false);
  const [_uploadModalPatient, _setUploadModalPatient] = useState<PatientModel | null>(null);

  // Relative date filter state - initialize as null, will be set from worklist or user interaction
  const [relativeDateFilterField, setRelativeDateFilterField] = useState<RelativeDateFilterField | null>(null);
  const [relativeDateFilterType, setRelativeDateFilterType] = useState<RelativeDateFilterType | null>(null);

  // Enhanced date range filter state
  const [isDateRangeFilterOpen, setIsDateRangeFilterOpen] = useState(false);
  const [dateRangeFilterValue, setDateRangeFilterValue] = useState<DateRangeFilterValue | null>(null);

  // Date range column menu state
  const [dateRangeColumnStates, setDateRangeColumnStates] = useState<Record<string, unknown>>({});

  // Check if ShowFilesTab setting is enabled
  const showFilesTab = useMemo(
    () => SettingsHelpers.findSettingByName(userSettings ?? [], WellKnownSettingName.ShowFilesTab, false)?.value === 'true',
    [userSettings],
  );

  // Get relative date filter parameters for API calls
  const relativeDateParams = useMemo(() => {
    // First check if there are relative date filters in the selected worklist
    const selectedWorklist = allWorklists?.find((w) => w.id === selectedWorklistId);
    const worklistParams = ExamsGridService.getRelativeDateApiParamsFromWorklist(selectedWorklist?.dataState ?? null);

    // If worklist has relative date filters, use those
    if (worklistParams.relativeDateField && worklistParams.relativeDateType) {
      return worklistParams;
    }

    // Otherwise use the current UI state
    if (relativeDateFilterField && relativeDateFilterType) {
      return {
        relativeDateField: relativeDateFilterField,
        relativeDateType: relativeDateFilterType,
      };
    }

    return {};
  }, [allWorklists, selectedWorklistId, relativeDateFilterField, relativeDateFilterType]);

  // Debounce only the search value to avoid UI locking on immediate actions
  const debouncedSearchValue = useDebounce(searchValue, 500);

  // No longer need transitioning state - TanStack Query handles deduplication

  // Get selected worklist for reconstruction (memoized separately to avoid triggering queryDataState recalculation)
  const selectedWorklist = useMemo(() => {
    if (!selectedWorklistId || !allWorklists) return null;
    return allWorklists.find((w) => w.id === selectedWorklistId) ?? null;
  }, [selectedWorklistId, allWorklists]);

  // Optimized query data state with stable dependencies only
  const queryDataState = useMemo(() => {
    // Early return if no data state available
    if (!dataState) {
      return null;
    }

    // Remove the selectedWorklistId logging as it causes unnecessary logs and is not used in calculation
    console.log('[queryDataState] Building query state');

    // Build state with search
    let state = hasText(debouncedSearchValue) ? ExamsGridService.getQueryDataState(dataState, debouncedSearchValue) : cloneDeep(dataState);

    // Apply UI relative date filters if active (these come from worklist or user interaction)
    if (relativeDateFilterField && relativeDateFilterType) {
      state = ExamsGridService.applyRelativeDateFilter(state, relativeDateFilterField, relativeDateFilterType);
    }

    // Ensure default active filter exists
    if (!state?.filter?.filters || state.filter.filters.length === 0) {
      state.filter = {
        logic: 'and',
        filters: [{ field: 'active', operator: 'eq', value: true }],
      };
    }

    // Add location filter
    if (state?.filter?.filters && sessionLocation?.id) {
      // Remove existing location filters
      state.filter.filters = state.filter.filters.filter((f) => !('field' in f && f.field === 'location_Id'));
      // Add current location filter
      state.filter.filters.push({
        field: 'location_Id',
        operator: 'eq',
        value: sessionLocation.id,
      });
    }

    return state;
  }, [dataState, debouncedSearchValue, relativeDateFilterField, relativeDateFilterType, sessionLocation?.id]);

  // Stable memo for files requirement to prevent unnecessary recalculations
  const needsFiles = useMemo(() => {
    // Check if any file-related columns are visible
    const fileRelatedColumns = ['files', 'hasExamFiles', 'hasAttachments', 'hasNonExamFiles', 'hasFinalReport'];

    const hasVisibleFileColumns = fileRelatedColumns.some((column) => columnsState?.[column]?.show === true);

    // Patient view always needs files for action button states (View Exam, Attachments, Final Report)
    if (featureView === 'Patient') {
      return true;
    }

    // Share view always needs files for action button states
    if (featureView === 'Share') {
      return true;
    }

    // For Physician view, only include files if showFilesTab is enabled AND file columns are visible
    if (featureView === 'Physician') {
      return showFilesTab && hasVisibleFileColumns;
    }

    // For Exam view, only include if file-related columns are actually visible
    return hasVisibleFileColumns;
  }, [featureView, columnsState, showFilesTab]);

  // Memoize auth mode to prevent recalculation
  const authMode = useMemo(() => {
    return activeScheme != null && activeScheme === AuthenticationScheme.OIDC ? 'msal-required' : 'share-required';
  }, [activeScheme]);

  // Use TanStack Query for exam data fetching
  const examsQuery = useExamsGridQuery({
    dataState: queryDataState,
    authMode,
    worklistViewId: selectedWorklistId ?? null,
    columnsState,
    enabled: queryDataState != null,
    refetchInterval: isPageVisible ? AUTO_REFRESH_INTERVAL : undefined,
    includeFiles: needsFiles,
    relativeDateParams,
  });

  const exams = examsQuery.data;

  // Generate dynamic sort options based on the ExamGridModel and visible columns
  const sortOptions = useMemo(() => {
    if (!exams?.data || exams.data.length === 0 || !columnsState) {
      return [];
    }

    // Use the first exam as a sample model for type inference
    const sampleModel = exams.data[0];
    return WorklistSortService.generateSortOptionsFromModel(columnsState, sampleModel);
  }, [exams?.data, columnsState]);

  // Get current sort option from data state
  const selectedSortOption = useMemo(() => {
    return WorklistSortService.getCurrentSortOption(dataState, sortOptions);
  }, [dataState, sortOptions]);

  // Handle sort change from sidebar
  const handleSortChange = useEvent((sortOption: { label: string; field: string; direction: 'asc' | 'desc' }) => {
    if (!dataState || !sortOption) return;

    const newDataState = WorklistSortService.applySortToDataState(dataState, sortOption);
    setDataState(newDataState);

    // Save the updated state
    ExamsGridService.saveExamGridState({
      worklistViewId: selectedWorklistId ?? null,
      dataState: newDataState,
      columnsState,
      search: hasText(searchValue) ? searchValue.trim() : null,
    });
  });

  // Handle relative date filter changes (temporary UI state only)
  const handleRelativeDateFilterChange = useEvent((field: RelativeDateFilterField, filterType: RelativeDateFilterType) => {
    setRelativeDateFilterField(field);
    setRelativeDateFilterType(filterType);

    // Trigger a refresh to apply the new filter (temporary, not saved to worklist)
    refreshExams();
    fetchCounts(false); // Also refresh counts to reflect the new filter
  });

  const handleRelativeDateFilterClear = useEvent(() => {
    setRelativeDateFilterField(null);
    setRelativeDateFilterType(null);

    // Trigger a refresh to remove the filter (temporary, not saved to worklist)
    refreshExams();
    fetchCounts(false); // Also refresh counts to reflect the removed filter
  });

  // Enhanced date range filter handlers
  const handleDateRangeFilterChange = useEvent((filterValue: DateRangeFilterValue) => {
    // Convert to the expected format for the existing relative date filter system
    if (filterValue.type === 'custom') {
      // For custom ranges, apply them directly to the data state
      setDateRangeFilterValue(filterValue);

      // Apply the custom date range filter to the current data state
      if (dataState) {
        const newDataState = applyDateRangeFilterToDataState(dataState, 'dateCreated', filterValue);
        setDataState(newDataState);
      }

      // Clear relative date filters since we're using a custom range
      setRelativeDateFilterField(null);
      setRelativeDateFilterType(null);
    } else {
      // For predefined ranges, use the existing logic
      setRelativeDateFilterField('dateCreated'); // or the appropriate field
      setRelativeDateFilterType(filterValue.type);

      // Clear custom date range value since we're using a relative filter
      setDateRangeFilterValue(null);
    }

    refreshExams();
    fetchCounts(false);
  });

  const handleDateRangeFilterClear = useEvent(() => {
    setDateRangeFilterValue(null);
    setRelativeDateFilterField(null);
    setRelativeDateFilterType(null);
    refreshExams();
    fetchCounts(false);
  });

  const handleDateRangeFilterOpen = useEvent(() => {
    setIsDateRangeFilterOpen(true);
  });

  const handleDateRangeFilterClose = useEvent(() => {
    setIsDateRangeFilterOpen(false);
  });

  // Manual refresh function for cases where we need to force refetch
  const refreshExams = useEvent(() => {
    examsQuery.refetch();
  });

  // Legacy fetchExams function for compatibility with existing code
  const fetchExams = useEvent(async (_showLoadingSpinner?: boolean, _dataStateOverride?: State) => {
    // TanStack Query handles the actual fetching, just trigger a refetch
    await examsQuery.refetch();
  });

  // Fetch worklist counts
  const fetchCounts = useEvent(async (forceFetch: boolean) => {
    if (allWorklists == null || allWorklists.length === 0) {
      return;
    }

    const worklistIds = allWorklists.map((w) => w.id);

    try {
      // Extract relative date filter from current state or selected worklist
      let relativeDateField: string | undefined;
      let relativeDateType: string | undefined;

      // If we have active relative date filters in state, use those
      if (relativeDateFilterField && relativeDateFilterType) {
        relativeDateField = relativeDateFilterField;
        relativeDateType = relativeDateFilterType;
      } else if (selectedWorklistId && allWorklists) {
        // Otherwise, check if the selected worklist has relative date filters in its dataState
        const selectedWorklist = allWorklists.find((w) => w.id === selectedWorklistId);
        if (selectedWorklist?.dataState) {
          try {
            const dataState = typeof selectedWorklist.dataState === 'string' ? JSON.parse(selectedWorklist.dataState) : selectedWorklist.dataState;

            if (dataState.relativeDateFilters && Array.isArray(dataState.relativeDateFilters) && dataState.relativeDateFilters.length > 0) {
              const filter = dataState.relativeDateFilters[0];
              relativeDateField = filter.field;
              relativeDateType = filter.filterType;
            }
          } catch (error) {
            console.warn('Failed to parse worklist dataState for relative date filters:', error);
          }
        }
      }

      const counts = await apiClient.workListViewClient.getExamStatusCount(
        worklistIds,
        null,
        undefined, // patientId
        relativeDateField,
        relativeDateType,
      );
      setAllCounts(counts);
    } catch (error) {
      console.error('Error fetching worklist counts:', error);
      NotificationsService.displayError('Failed to load worklist counts.');
    }
  });

  const initialize = useEvent(async () => {
    const newWorklists = await apiClient.workListViewClient.getViews(false, sessionLocation?.id, 'Exam');

    const examGridState = ExamsGridService.initializeExamGridState(newWorklists, currentUser.role, pickDefaultWorklist, isMobile);

    const { newDataState, newColumnsState, newSearchValue } = examGridState;
    let newSelectedWorklist = examGridState.newSelectedWorklist;

    const topLevelWorklists = newWorklists.filter((w) => w.parentViewId == null);

    // Use consolidated worklist visibility logic
    const { shouldExpand, reason } = determineWorklistSidebarState(isMobile, newWorklists, true);
    const shouldCollapse = !shouldExpand;

    if (shouldCollapse && topLevelWorklists.length > 0) {
      newSelectedWorklist = topLevelWorklists[0];
    }

    console.log('[BaseExamGridPage] Worklist sidebar initialization:', { reason, shouldCollapse, worklistCount: newWorklists.length });

    const initialPanesState = ExamsGridService.initializeExamSplitterState(shouldCollapse);

    // Set core state first to prevent undefined dataState issues
    setAllWorklists(newWorklists);
    setPanes(initialPanesState);
    setDataState(newDataState);
    setColumnsState(newColumnsState);
    setSearchValue(newSearchValue);

    // Set selectedWorklistId last to ensure dataState is available when queryDataState recalculates
    startTransition(() => {
      setSelectedWorklistId(newSelectedWorklist?.id ?? null);
    });

    // Note: fetchCounts will be called automatically by useEffect once allWorklists state is set
  });

  const editWindow = useExamEditWindow({ onBaseGridUpdate: fetchExams });

  useEffect(() => {
    if (dataState) {
      const oDataFilterString = toDataSourceRequestString(dataState);
      localStorage.setItem('currentWorklistViewODataStr', oDataFilterString);
    } else {
      localStorage.removeItem('currentWorklistViewODataStr');
    }
  }, [dataState]);

  useEffect(() => {
    window.addEventListener('message', (event) => {
      if (event.data === 'examClosed') {
        fetchExams(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReadClick = useEvent(async (dataItem: ExamGridModel) => {
    // open exam edit in new window for dual screen work and to not lose grid state
    navigate(`/exam/${dataItem.id}/read`);
  });

  const handleFilesClick = useEvent(async (dataItem: ExamGridModel) => {
    // Navigate to patient files tab when ShowFilesTab setting is enabled
    if (dataItem.patientId) {
      navigate(PatientRoutes.getFiles(dataItem.patientId));
    }
  });

  const handleAddNewClick = useEvent(() => {
    // open exam add in new window for dual screen work and to not lose grid state
    window.open(`/exam/add`, 'AddExam', 'menubar=0,status=0,fullscreen=1');
  });

  const handleUploadClick = useEvent(() => {
    if (reactUploadPage) {
      navigate('/upload/' + (sessionLocation == null ? '' : `?sessionLocationId=${sessionLocation.id}`));
    } else {
      window.location.href = `${legacyBaseUrl}/DICOM/UploadDICOM`;
    }
  });

  const handleEditClick = useEvent((dataItem: ExamGridModel) => {
    // open exam edit in new window for dual screen work and to not lose grid state
    window.open(`/exam/${dataItem.id}/edit`, 'EditExam', 'menubar=0,status=0,fullscreen=1');
  });

  const handleViewDicomImages = useEvent((dataItem: ExamGridModel) => {
    window.open(`/file-viewer/${dataItem.id}?feature=exams`, '_blank');
  });

  const handleShareGridClick = useEvent((exam: ExamGridModel | null | undefined) => {
    if (exam == null) return;
    if (exam.id == null) throw new Error('Cannot share an exam that does not have an ID.');
    if (exam.patientId == null) throw new Error('Cannot share an exam that does not have a patient ID.');

    setShareModalVisible();
    setShareExamId(exam.id);
    setSharePatientId(exam.patientId);
  });

  const handleSharePatientChange = useEvent((_newPatient: PatientModel) => {
    // Refresh the data to get updated patient info
    refreshExams();
  });

  const handleViewAttachments = useEvent((dataItem?: ExamGridModel) => {
    setSelectedExamId(dataItem?.id ?? 0);
    setSelectedExamStatus(dataItem?.statusType ?? '');
    // toggle the donor diaglog
    setIsDonorViewFilesVisible(!isDonorViewFilesVisible);
  });

  const handleViewFinalReport = useEvent((dataItem: ExamGridModel) => {
    const finalReport =
      dataItem.files?.find(
        (fileItem: FileModel) => fileItem.fileName?.toLowerCase() === 'finalreport.pdf' || fileItem.categoryId === DocumentCategories.FINAL_REPORT.value,
      ) ?? null;
    if (finalReport) setFinalReport(finalReport);
    setIsDonorViewFinalReportVisible(!isDonorViewFinalReportVisible);
  });

  const handleReadRequestClick = useEvent((dataItem: ExamGridModel) => {
    if (featureView === 'Exam') {
      navigate(`/exam/${dataItem.id}/read-request`);
    } else {
      throw new Error(`handleReadRequestClick() is not implemented for featureView: "${featureView as string}".`);
    }
  });

  const handleManagePatientClick = useEvent((dataItem: ExamGridModel) => {
    if (reactPatientFormPage) {
      navigate(`/patient-2/edit/${dataItem.patientId}`);
    } else {
      window.location.href = `${legacyBaseUrl}/patient/edit/${dataItem.patientId}`;
    }
  });

  const handleRowDoubleClick = useEvent((dataItem: ExamGridModel) => {
    if (featureView === 'Exam') {
      handleEditClick(dataItem);
    } else if (featureView === 'Physician') {
      // Use files navigation if ShowFilesTab is enabled, otherwise use read
      if (showFilesTab) {
        handleFilesClick(dataItem);
      } else {
        handleReadClick(dataItem);
      }
    } else {
      throw new Error(`handleRowDoubleClick() is not implemented for featureView: "${featureView as string}".`);
    }
  });

  const handleDataStateChange = useEvent((event: ExamGridDataStateChangeEvent) => {
    setDataState(event.dataState);

    // Note: No need to manually call refreshExams() for 'now' refresh expectation
    // because React Query will automatically refetch when dataState changes (it's in the queryKey).
    // This prevents double API calls on sorting/paging.
    if (event.refreshExpectation === 'now') {
      // React Query will automatically refetch due to dataState change
    }

    if (['browser-only', 'browser-and-worklist'].includes(event.saveExpectation)) {
      ExamsGridService.saveExamGridState({
        worklistViewId: selectedWorklistId,
        dataState: event.dataState,
        columnsState,
        search: hasText(searchValue) ? searchValue.trim() : null,
      });
    }

    // TODO: Implement this.
    if (event.saveExpectation === 'browser-and-worklist') {
      throw new Error('TODO: Implement saving data state to worklist.');
    }
  });

  const handleColumnsStateChange = useEvent(async (event: ExamGridColumnsStateChangeEvent) => {
    setColumnsState(event.columnsState);

    if (['browser-only', 'browser-and-worklist'].includes(event.saveExpectation)) {
      ExamsGridService.saveExamGridState({
        worklistViewId: selectedWorklistId,
        dataState,
        columnsState: event.columnsState,
        search: hasText(searchValue) ? searchValue.trim() : null,
      });
    }

    // TODO: Implement this.
    if (event.saveExpectation === 'browser-and-worklist') {
      throw new Error('TODO: Implement saving columns state to worklist.');
    }
  });

  const handleExportCSVClick = useEvent(async () => {
    if (dataState == null) {
      throw new Error('Cannot export CSV because the dataState is null.');
    }

    const exportRequest = {
      format: 'csv',
      columns: columnsState ? Object.keys(columnsState).filter((col) => columnsState[col]?.show) : undefined,
    };

    const exportResponse = await apiClient.exams.exportExamsForKendoGrid(dataState, exportRequest, selectedWorklistId);

    // Use the download URL to trigger the download
    const link = document.createElement('a');
    link.href = exportResponse.downloadUrl;
    link.download = exportResponse.fileName;
    link.click();
    link.remove();
  });

  const handleBulkCancel = useEvent(async (data: SelectedState, cancelReason: string | null) => {
    const changes: ChangeExamStatusModel[] = Object.keys(data)
      .filter((key) => data[key])
      .map((key) => ({
        id: parseInt(key, 10),
        status: null,
        cancelReason: hasText(cancelReason) ? cancelReason.trim() : null,
      }));

    await apiClient.exams.bulkCancel(changes);

    NotificationsService.displaySuccess(`${changes.length === 1 ? 'Exam' : 'Exams'} cancelled successfully.`);
    fetchExams(true);
    fetchCounts(false);
  });

  const handleBulkAssignSubmit = useEvent(async (physicianId: number) => {
    const examIds = Object.entries(selectedExams)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_key, isSelected]) => isSelected)
      .map(([key]) => parseInt(key, 10));

    await apiClient.exams.bulkAssignDoctor(examIds, physicianId);
    NotificationsService.displaySuccess(`${examIds.length === 1 ? 'Exam' : 'Exams'} assigned successfully.`);

    fetchExams(true);
    fetchCounts(false);
  });

  const handleChangeStatusSubmit = useEvent(async (change: ChangeExamStatusModel) => {
    await apiClient.exams.changeStatus(change);

    NotificationsService.displaySuccess('Exam status changed successfully.');
    fetchExams(true);
    fetchCounts(false);
  });

  const handleSearchChange = useEvent((newSearchValue: string) => {
    setSearchValue(newSearchValue);

    // Save the grid state with the new search value
    ExamsGridService.saveExamGridState({
      worklistViewId: selectedWorklistId,
      dataState,
      columnsState,
      search: hasText(newSearchValue) ? newSearchValue.trim() : null,
    });
  });

  const handleSearchSubmit = useEvent(async () => {
    // No need for manual debounce clearing since we're using useDebounce from @uidotdev/usehooks
    // The search will automatically trigger when debouncedSearchValue changes
  });

  const handleWorklistsRefreshClick = useEvent(async () => {
    // For better responsiveness we are going to immediately update the worklist counts, but we also need to refresh the lists of worklists in case a different user added one.
    // We do not need to await for the fetchCounts() to complete to go ahead and fetch the new worklists because they are independent operations.
    fetchCounts(true);

    const newWorklists = await apiClient.workListViewClient.getViews(false, sessionLocation?.id, 'Exam');

    setAllWorklists(newWorklists);

    // Check if any worklists were added or removed.  Because we will have to refresh the counts an additional time to make sure we have counts for the new worklists.
    const oldWorklistIds = sortBy(uniq(allWorklists?.map((w) => w.id) ?? []));
    const newWorklistIds = sortBy(uniq(newWorklists.map((w) => w.id)));

    if (!isEqual(oldWorklistIds, newWorklistIds)) {
      fetchCounts(true);
    }
  });

  // Cache parsed worklist states to avoid expensive re-parsing
  const parsedWorklistStatesRef = useRef<Map<number, { dataState: State; columnsState: Record<string, ColumnState>; searchValue: string }>>(new Map());

  // Clear cache when worklists change to ensure consistency
  useEffect(() => {
    parsedWorklistStatesRef.current.clear();
  }, [allWorklists]);

  // Simple worklist selection handler - immediate UI feedback with optimized data loading
  const handleSelectedWorklistIdChange = useEvent((worklistId: number | null) => {
    const prevWorklistId = selectedWorklistId;

    // Prevent duplicate selections
    if (worklistId === prevWorklistId) {
      return;
    }

    console.log('[worklist selection] Changing from:', prevWorklistId, 'to:', worklistId);

    // 1. Update selected worklist IMMEDIATELY for instant UI feedback (outside of transition)
    setSelectedWorklistId(worklistId);
    setSelectedExams({});

    // 2. Apply data state changes in a transition (can be batched/deferred)
    startTransition(() => {
      setIsWorklistChanging(true);

      // Get and apply new data state from worklist (with caching)
      if (worklistId && allWorklists) {
        let cachedState = parsedWorklistStatesRef.current.get(worklistId);

        if (!cachedState) {
          // Only parse if not cached - this is the expensive operation
          const { newDataState, newColumnsState, newSearchValue } = ExamsGridService.updateStateFromWorklist(
            worklistId,
            currentUser.role,
            allWorklists,
            null,
            isMobile,
          );
          cachedState = {
            dataState: newDataState,
            columnsState: newColumnsState,
            searchValue: newSearchValue,
          };
          parsedWorklistStatesRef.current.set(worklistId, cachedState);
          console.log('[worklist selection] Parsed and cached state for worklist:', worklistId);
        } else {
          console.log('[worklist selection] Using cached state for worklist:', worklistId);
        }

        // 3. Apply data and column states
        setDataState(cachedState.dataState);
        setColumnsState(cachedState.columnsState);
        setSearchValue(cachedState.searchValue || '');

        // 4. Initialize relative date filter state from worklist
        const selectedWorklist = allWorklists.find((w) => w.id === worklistId);
        if (selectedWorklist?.dataState) {
          try {
            const dataState = JSON.parse(selectedWorklist.dataState);
            if (dataState.relativeDateFilters && Array.isArray(dataState.relativeDateFilters) && dataState.relativeDateFilters.length > 0) {
              const filter = dataState.relativeDateFilters[0];
              setRelativeDateFilterField(filter.field);
              setRelativeDateFilterType(filter.filterType);
              console.log('[worklist selection] Initialized relative date filter from worklist:', filter);
            } else {
              // Clear relative date filter state if worklist doesn't have filters
              setRelativeDateFilterField(null);
              setRelativeDateFilterType(null);
              console.log('[worklist selection] Cleared relative date filter state - no filters in worklist');
            }
          } catch (error) {
            console.warn('[worklist selection] Failed to parse worklist dataState for relative date filters:', error);
            setRelativeDateFilterField(null);
            setRelativeDateFilterType(null);
          }
        } else {
          // Clear relative date filter state if no dataState
          setRelativeDateFilterField(null);
          setRelativeDateFilterType(null);
        }
      } else {
        // No worklist selected - clear relative date filter state
        setRelativeDateFilterField(null);
        setRelativeDateFilterType(null);
      }

      setIsWorklistChanging(false);
    });
  });

  // Clear isWorklistChanging when the query finishes loading
  useEffect(() => {
    if (!examsQuery.isLoading && !examsQuery.isFetching) {
      setIsWorklistChanging(false);
    }
  }, [examsQuery.isLoading, examsQuery.isFetching]);

  const handleCreateWorklist = useEvent(async (model: WorklistViewModel) => {
    if (allWorklists == null) return; // Don't do anything if the worklists haven't been loaded yet.

    const newWorklist = await apiClient.workListViewClient.createView(model);

    const { newDataState, newColumnsState, newSearchValue } = ExamsGridService.updateStateFromWorklist(
      newWorklist.id,
      currentUser.role,
      [newWorklist],
      null,
      isMobile,
    );

    // Batch all state updates together to prevent multiple API calls
    startTransition(() => {
      setSelectedWorklistId(newWorklist.id);
      setDataState(newDataState);
      setColumnsState(newColumnsState);
      setSearchValue(newSearchValue);
    });

    fetchCounts(false);
    // Note: fetchExams is not needed here - TanStack Query will automatically refetch when selectedWorklistId and queryDataState change

    // Refresh the list of worklists.
    const newWorklists = await apiClient.workListViewClient.getViews(false, sessionLocation?.id, 'Exam');
    setAllWorklists(newWorklists);
  });

  const handleUpdateWorklist = useEvent(async (worklists: WorklistViewModel[]) => {
    await Promise.all(worklists.map((w) => apiClient.workListViewClient.updateView(w)));

    // Invalidate cache for updated worklists
    worklists.forEach((w) => {
      parsedWorklistStatesRef.current.delete(w.id);
    });

    // Refresh the exams grid if the selected worklist was modified.
    const selectedWorklist = worklists.find((w) => w.id === selectedWorklistId);

    if (selectedWorklist != null) {
      const { newDataState, newColumnsState, newSearchValue } = ExamsGridService.updateStateFromWorklist(
        selectedWorklist.id,
        currentUser.role,
        [selectedWorklist],
        null,
        isMobile,
      );

      // Update cache with new state
      parsedWorklistStatesRef.current.set(selectedWorklist.id, {
        dataState: newDataState,
        columnsState: newColumnsState,
        searchValue: newSearchValue,
      });

      // Batch all state updates together to prevent multiple API calls
      startTransition(() => {
        setDataState(newDataState);
        setColumnsState(newColumnsState);
        setSearchValue(newSearchValue);
      });

      // Note: fetchExams is not needed here - TanStack Query will automatically refetch when queryDataState changes
    }

    // Refresh the worklists.
    const newWorklists = await apiClient.workListViewClient.getViews(false, sessionLocation?.id, 'Exam');

    setAllWorklists(newWorklists);

    fetchCounts(false); // We don't need to await for the fetch counts to complete.
  });

  const handleDeleteWorklist = useEvent(async (worklist: WorklistViewModel) => {
    await apiClient.workListViewClient.deleteView(worklist.id);

    if (selectedWorklistId === worklist.id) {
      // The user deleted the currently selected worklist.  Reset the grid state.
      const { newDataState, newColumnsState, newSearchValue } = ExamsGridService.updateStateFromWorklist(null, currentUser.role, [], null, isMobile);

      ExamsGridService.saveExamGridState(null);

      // Batch all state updates together to prevent multiple API calls
      startTransition(() => {
        setSelectedWorklistId(null);
        setDataState(newDataState);
        setColumnsState(newColumnsState);
        setSearchValue(newSearchValue);
      });

      // Note: fetchExams is not needed here - TanStack Query will automatically refetch when selectedWorklistId and queryDataState change
    }

    setAllWorklists((prev) => prev?.filter((w) => w.id !== worklist.id) ?? []);
  });

  const handleSidebarExpandChange = useEvent((isExpanded: boolean) => {
    if (panes == null) throw new Error('Cannot proceed because panes is null.');

    const newPanesState = cloneDeep(panes);
    newPanesState[0].collapsed = !isExpanded;

    setPanes(newPanesState);
    ExamsGridService.saveExamSplitterState(newPanesState);
  });

  const handleSidebarExpandClick = useEvent(() => {
    handleSidebarExpandChange(true);
  });

  const handleFinalReportClosed = useEvent(() => {
    setIsDonorViewFinalReportVisible(false);
  });

  const handleSplitterChange = useEvent((event: SplitterOnChangeEvent) => {
    setPanes(event.newState);

    ExamsGridService.saveExamSplitterState(event.newState);
  });

  const selectedWorklistName = useMemo(() => {
    if (!selectedWorklistId || !allWorklists) return null;
    return allWorklists.find((w) => w.id === selectedWorklistId)?.name ?? null;
  }, [selectedWorklistId, allWorklists]);

  const SlotToolbarPrefix = useMemo(
    () => <WorklistSidebarCollapsed handleShowSidebar={handleSidebarExpandClick} selectedWorklistName={selectedWorklistName} />,
    [handleSidebarExpandClick, selectedWorklistName],
  );

  useEffect(() => {
    initialize();
  }, [initialize]);

  const areWorklistsInitialized = allWorklists != null && allWorklists.length > 0;

  // Note: Removed manual fetchExams useEffect - React Query automatically handles refetching
  // when queryDataState changes due to dataState, searchValue, selectedWorklistId, or sessionLocation changes

  // Fetch counts once worklists are loaded
  useEffect(() => {
    if (!areWorklistsInitialized) return;

    fetchCounts(true);
  }, [areWorklistsInitialized, fetchCounts]);

  useInterval(
    () => {
      fetchCounts(false);
    },
    isPageVisible ? AUTO_REFRESH_INTERVAL : null,
  ); // Only auto-refresh counts when tab is visible

  // Add SignalR integration for automatic grid refresh
  useSignalRQueryInvalidation({
    // Smart invalidation based on current context
    enableSmartInvalidation: true,
    enableDebugLogging: true,
    debounceMs: 500, // Debounce to prevent rapid-fire updates

    // Only invalidate queries when the page is visible to avoid unnecessary API calls
    invalidateOnExamChanged: isPageVisible ? ['exams-grid-optimized'] : false,
    invalidateOnPatientChanged: isPageVisible ? ['exams-grid-optimized'] : false,
    invalidateOnFileChanged: isPageVisible ? ['exams-grid-optimized'] : false, // Files can affect exam display
    invalidateOnWorklistChanged: isPageVisible ? ['exams-grid-optimized'] : false,
    invalidateOnSystemDataChanged: isPageVisible ? ['exams-grid-optimized'] : false,
  });

  if (dataState == null || columnsState == null || panes == null) {
    return null;
  }

  return (
    <StyledPage>
      <PageHeader showSessionLocation title={title} />

      <ExcelExport data={exams?.data} ref={gridExcelExport} />
      <StyledSplitter panes={panes} orientation="horizontal" onChange={handleSplitterChange}>
        <WorklistSidebar
          autoRefreshInterval={AUTO_REFRESH_INTERVAL}
          allWorklists={allWorklists}
          allCounts={allCounts}
          showSidebar={true}
          selectedWorklistId={selectedWorklistId}
          currentDataState={dataState}
          currentColumnsState={columnsState}
          onShowSidebarChange={handleSidebarExpandChange}
          onSelectedWorklistIdChange={handleSelectedWorklistIdChange}
          onRefreshClick={handleWorklistsRefreshClick}
          onCreateWorklist={handleCreateWorklist}
          onUpdateWorklist={handleUpdateWorklist}
          onDeleteWorklist={handleDeleteWorklist}
          filterType="field"
          patientId={null}
          sortOptions={sortOptions}
          selectedSortOption={selectedSortOption}
          onSortChange={handleSortChange}
          relativeDateFilterField={relativeDateFilterField}
          relativeDateFilterType={relativeDateFilterType}
          onRelativeDateFilterChange={handleRelativeDateFilterChange}
          onRelativeDateFilterClear={handleRelativeDateFilterClear}
        />
        <ExamGrid
          featureView={featureView}
          columnsState={columnsState}
          dataState={dataState}
          data={exams ?? null}
          showActionColumn
          showExamToolbar={showExamToolbar}
          isLoading={examsQuery.isLoading || examsQuery.isFetching || isWorklistChanging}
          slotToolbarPrefix={panes[0].collapsed ? SlotToolbarPrefix : undefined}
          selectable={selectable}
          selectedExams={selectedExams}
          filterSearch={searchValue}
          relativeDateFilterField={relativeDateFilterField}
          relativeDateFilterType={relativeDateFilterType}
          onRelativeDateFilterChange={handleRelativeDateFilterChange}
          onRelativeDateFilterClear={handleRelativeDateFilterClear}
          onExamReadClick={handleReadClick}
          onExamFilesClick={handleFilesClick}
          showFilesTab={showFilesTab}
          onDataStateChange={handleDataStateChange}
          onColumnsStateChange={handleColumnsStateChange}
          onAddNewClick={handleAddNewClick}
          onSelectedExamsChange={setSelectedExams}
          onExamEditClick={handleEditClick}
          onExamReadRequestClick={handleReadRequestClick}
          onExamManagePatientClick={handleManagePatientClick}
          onExamDoubleClick={handleRowDoubleClick}
          onExportCsvClick={handleExportCSVClick}
          onBulkCancelSubmit={handleBulkCancel}
          onBulkChangeStatusSubmit={handleChangeStatusSubmit}
          onBulkAssignSubmit={handleBulkAssignSubmit}
          onFilterSearchChange={handleSearchChange}
          onFilterSearchSubmit={handleSearchSubmit}
          onExamViewClick={handleViewDicomImages}
          onExamViewAttachmentsClick={handleViewAttachments}
          onExamShareClick={handleShareGridClick}
          onExamViewFinalReportClick={handleViewFinalReport}
          onExamEditWindowClick={editWindow.openExamEditWindow}
          onUploadClick={handleUploadClick}
        />
      </StyledSplitter>
      {isDonorViewFilesVisible && <UploadWindow onClose={handleViewAttachments} examId={selectedExamId} onChange={() => fetchExams(false)}></UploadWindow>}
      {isDonorViewFinalReportVisible && <ViewFileWindow title="Final Report" file={finalReport} onClose={handleFinalReportClosed} />}
      {sharePatientId && (
        <ShareModal
          patientId={sharePatientId}
          examId={shareExamId}
          show={isShareModalVisible}
          onHide={setShareModalHidden}
          onPatientChange={handleSharePatientChange}
        />
      )}
      {editWindow?.selectedExamId && editWindow.showExamEditWindow && editWindow.selectedPatientId && (
        <ExamEditWindow
          onClosed={editWindow.closeExamEditWindow}
          patientId={editWindow.selectedPatientId}
          examId={editWindow.selectedExamId}
          onDeleteShareClick={editWindow.handleDeleteShareClick}
          onExamFormSubmit={editWindow.handleFormSubmit}
        />
      )}

      {/* Enhanced Date Range Filter Modal */}
      <DateRangeFilter
        value={dateRangeFilterValue}
        onChange={handleDateRangeFilterChange}
        onClear={handleDateRangeFilterClear}
        isOpen={isDateRangeFilterOpen}
        onClose={handleDateRangeFilterClose}
        title="Filter Date Range"
      />
    </StyledPage>
  );
});

BaseExamGridPage.displayName = 'BaseExamGridPage';

const StyledPage = styled(Page)`
  /* Ensure Page component maintains proper layout and constrains height */
  grid-template-rows: min-content minmax(0, 1fr);
  min-height: 100vh;
  max-height: 100vh; /* Constrain to viewport height */
  overflow: hidden;
`;

const StyledSplitter = styled(Splitter)`
  --kendo-color-primary: ${({ theme }) => theme.colors.primary};

  /* Apply Pattern 2: Fill Available Space (Scrollable Overflow) from CSS guide */
  display: flex;
  flex-direction: row;
  min-height: 0; /* Critical: Allow shrinking */
  overflow: hidden;

  .k-pane.k-hidden + .k-splitbar {
    display: none;
  }

  .worklist-pane {
    display: flex;
    flex-direction: column;
    flex: 0 0 auto; /* Fixed width sidebar */
  }

  .grid-pane {
    display: flex;
    flex-direction: column;
    flex: 1 1 0; /* Fill remaining space */
    min-height: 0; /* Allow shrinking */
    overflow: hidden;
  }

  /* Fix the Kendo Grid flexbox hierarchy - Pattern 2 application */
  .k-grid {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
  }

  .k-grid-aria-root {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
  }

  .k-grid-container {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto; /* Enable inner scrolling */
    height: auto; /* Override any explicit height */
  }

  /* Fix the critical grid content wrapper */
  .k-grid-content {
    flex: 1 1 0;
    min-height: 400px; /* Force minimum height to ensure visibility */
    overflow: visible; /* Don't clip table content */
    height: auto; /* Override any explicit height */
  }

  /* Ensure the grid itself participates in flex layout */
  .k-grid {
    display: flex;
    flex-direction: column;
    height: 100%; /* Take full available height */
    min-height: 400px; /* Force minimum height for the entire grid */
  }
`;
